import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const fadeUp = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] as const },
};

const pillars = [
  {
    number: "01",
    step: "Step 1",
    title: "Subfloor Evaluation & Engineering.",
    desc: "Before a single plank is discussed, we evaluate the surface we are installing upon. We assess structural integrity, flatness tolerances, and moisture levels of your plywood or concrete slab to determine what prep work is required.",
    link: "/subfloor-leveling",
    linkLabel: "Learn about Subfloor Engineering →",
  },
  {
    number: "02",
    step: "Step 2",
    title: "Climate Assessment & Moisture Testing.",
    desc: "A floor is a living architectural element. We evaluate how your home sits on its lot, your thermal envelope, and your specific HVAC and humidification systems to understand the exact environment the wood will live in.",
    link: "/environment-acclimation",
    linkLabel: "Learn about Environmental Control →",
  },
  {
    number: "03",
    step: "Step 3",
    title: "Hardwood Product Specification.",
    desc: "Only after evaluating your subfloor and climate do we specify a product. From solid wood to advanced engineered Baltic Birch cores, we help you select a floor scientifically guaranteed to perform in your home's unique ecosystem.",
    link: "/product-specification",
    linkLabel: "Read our Specification Guide →",
  },
  {
    number: "04",
    step: "Step 4",
    title: "Long Term Maintenance & Warranties.",
    desc: "A successful installation doesn't end when we leave. How you clean and maintain your floor dictates its lifespan. We provide you with the exact protocols, products, and NWFA guidelines to protect your investment and warranties for decades.",
    link: null,
    linkLabel: "View Maintenance Protocols →",
  },
];

const InstallationPillars = () => {
  return (
    <section className="bg-background py-14 md:py-20 lg:py-28">
      <div className="max-w-[1200px] mx-auto px-5 md:px-12">
        <motion.div {...fadeUp} className="mb-8 md:mb-12 max-w-2xl">
          <p className="text-[11px] font-semibold tracking-[.1em] uppercase text-[hsl(var(--gold-400))] mb-2 flex items-center gap-2">
            The 4 Pillars
            <span className="flex-1 h-px bg-border" />
          </p>
          <h2 className="font-display text-[clamp(28px,5vw,52px)] font-semibold text-foreground leading-[1.1]">
            The 4 Pillars of a{" "}
            <em className="italic font-normal text-primary">Lifelong Floor.</em>
          </h2>
          <p className="mt-4 text-sm md:text-[15px] font-light text-muted-foreground leading-relaxed">
            The actual fastening of the wood is only a small fraction of the job.
            Our consultation follows a strict chronological process. Each step informs the next, engineering a floor that will last for generations.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {pillars.map((pillar, i) => (
            <motion.div
              key={pillar.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.6 }}
              className="border border-border/50 rounded-2xl p-6 md:p-7 flex flex-col group hover:bg-foreground hover:border-foreground transition-colors duration-500"
            >
              <span className="text-[36px] md:text-[40px] font-display font-bold text-primary/15 leading-none mb-1 group-hover:text-primary/20 transition-colors duration-500">
                {pillar.number}
              </span>
              <p className="text-[10px] font-semibold tracking-[.12em] uppercase text-primary mb-2 transition-colors duration-500">
                {pillar.step}
              </p>
              <h4 className="font-display text-base md:text-lg font-semibold text-foreground group-hover:text-primary-foreground mb-2.5 leading-snug transition-colors duration-500">
                {pillar.title}
              </h4>
              <p className="text-[11px] md:text-[12px] font-light text-muted-foreground group-hover:text-primary-foreground/60 leading-relaxed flex-grow transition-colors duration-500">
                {pillar.desc}
              </p>
              {pillar.link ? (
                <Link
                  to={pillar.link}
                  className="mt-4 text-xs font-semibold tracking-[.06em] text-primary hover:text-[hsl(var(--gold-300))] transition-colors"
                >
                  {pillar.linkLabel}
                </Link>
              ) : (
                <span className="mt-4 text-xs font-semibold tracking-[.06em] text-muted-foreground/40 group-hover:text-primary-foreground/30 transition-colors duration-500 cursor-default">
                  {pillar.linkLabel}
                </span>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default InstallationPillars;
